/**
 * 2026-05-07 fix — taxonomy GETs were on `/api/taxonomy/*`. The audit
 * moved them to `/api/docs/taxonomy/*`; the old paths 404. The fall-out
 * was empty Type / Correspondent / Tag / Storage Location dropdowns
 * even on documents that had values set. Switched to the canonical
 * paths and added legacy fallbacks for the column names that drifted
 * (canonical: document_type_id / date_on_document / original_filename).
 *
 * The PATCH body still uses the legacy keys (`type_id`, `tag_ids`,
 * `date_received`, `notes`) — those silently get dropped by the
 * server's Zod schema today, which is a separate larger fix tracked
 * separately. This commit doesn't touch the write path.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FileText, Edit2, Save, X, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { authFetch } from "@/lib/auth";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import type { Document, DocType, Correspondent, Tag, StorageLocation } from "@shared/types";
function formatFileSize(bytes: number): string {
  if (!bytes) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
// Read fields with legacy fallbacks. Schema canonical names win when
// present; older / mock rows still render.
function typeIdOf(d: Document): string { return (d.document_type_id ?? d.type_id ?? "") as string; }
function dateOf(d: Document): string { return (d.date_on_document ?? d.date_received ?? "") as string; }
function fileNameOf(d: Document): string { return (d.original_filename ?? d.file_name ?? "") as string; }
export function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<Document | null>(null);
  const [types, setTypes] = useState<DocType[]>([]);
  const [correspondents, setCorrespondents] = useState<Correspondent[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  const [storageLocations, setStorageLocations] = useState<StorageLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eTitle, setETitle] = useState("");
  const [eTypeId, setETypeId] = useState("");
  const [eCorrId, setECorrId] = useState("");
  const [eTagIds, setETagIds] = useState<string[]>([]);
  const [eStorageId, setEStorageId] = useState("");
  const [eDateReceived, setEDateReceived] = useState("");
  const [eNotes, setENotes] = useState("");
  const typeMap = useMemo(() => new Map(types.map((t) => [t.id, t.name])), [types]);
  const corrMap = useMemo(() => new Map(correspondents.map((c) => [c.id, c.name])), [correspondents]);
  const tagMap = useMemo(() => new Map(tags.map((t) => [t.id, t.name])), [tags]);
  const slMap = useMemo(() => new Map(storageLocations.map((s) => [s.id, s.name])), [storageLocations]);
  const loadData = useCallback(async () => {
    if (!id) return;
    try {
      // Canonical taxonomy surface is /api/docs/taxonomy/* (audit §1).
      const [d, tData, cData, tagData, slData] = await Promise.all([
        authFetch<Document>(`/api/docs/${id}`),
        authFetch<{ items: DocType[] }>("/api/docs/taxonomy/types"),
        authFetch<{ items: Correspondent[] }>("/api/docs/taxonomy/correspondents"),
        authFetch<{ items: Tag[] }>("/api/docs/taxonomy/tags"),
        authFetch<{ items: StorageLocation[] }>("/api/docs/taxonomy/storage-locations"),
      ]);
      setDoc(d);
      setTypes(tData.items ?? []);
      setCorrespondents(cData.items ?? []);
      setTags(tagData.items ?? []);
      setStorageLocations(slData.items ?? []);
      setETitle(d.title ?? "");
      setETypeId(typeIdOf(d));
      setECorrId(d.correspondent_id ?? "");
      setETagIds(d.tag_ids ?? []);
      setEStorageId(d.storage_location_id ?? "");
      setEDateReceived(dateOf(d));
      setENotes(d.notes ?? "");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg);
      console.error("[DocumentDetail]", msg);
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => {
    loadData();
  }, [loadData]);
  const handleSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      const updated = await authFetch<Document>(`/api/docs/${id}`, {
        method: "PATCH",
        body: JSON.stringify({
          title: eTitle.trim(),
          type_id: eTypeId,
          correspondent_id: eCorrId,
          tag_ids: eTagIds,
          storage_location_id: eStorageId,
          date_received: eDateReceived,
          notes: eNotes.trim(),
        }),
      });
      setDoc(updated);
      setEditing(false);
      toast.success("Document updated");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to update");
      console.error("[DocSave]", err);
    } finally {
      setSaving(false);
    }
  };
  const handleArchive = async () => {
    if (!id) return;
    try {
      await authFetch(`/api/docs/${id}`, { method: "DELETE" });
      toast.success("Document archived");
      navigate("/documents");
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : "Failed to archive");
    }
  };
  const toggleTag = (tagId: string) => {
    setETagIds((prev) =>
      prev.includes(tagId) ? prev.filter((t) => t !== tagId) : [...prev, tagId]
    );
  };
  if (loading) {
    return (
      <>
        <PageHeader
          title=""
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Documents", href: "/documents" },
            { label: "Loading..." },
          ]}
        />
        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-32 w-full rounded-lg" />
        </div>
      </>
    );
  }
  if (error || !doc) {
    return (
      <>
        <PageHeader
          title="Document Not Found"
          breadcrumbs={[
            { label: "Dashboard", href: "/" },
            { label: "Documents", href: "/documents" },
            { label: "Error" },
          ]}
        />
        <div className="p-4 rounded-lg bg-destructive/10 text-destructive text-sm">
          {error || "Document not found"}
        </div>
      </>
    );
  }
  const docTypeId = typeIdOf(doc);
  const docDate = dateOf(doc);
  const docFile = fileNameOf(doc);
  return (
    <>
      <PageHeader
        title={doc.title}
        breadcrumbs={[
          { label: "Dashboard", href: "/" },
          { label: "Documents", href: "/documents" },
          { label: doc.title },
        ]}
      >
        {!editing ? (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Edit2 className="h-4 w-4 mr-1" /> Edit
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" size="sm" className="text-destructive border-destructive/30">
                  <Trash2 className="h-4 w-4 mr-1" /> Archive
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Archive "{doc.title}"?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will hide the document from active lists.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleArchive} className="bg-destructive text-destructive-foreground">
                    Archive
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-burnt-orange hover:bg-burnt-orange/90 text-white dark:bg-burnt-orange-dark dark:hover:bg-burnt-orange-dark/90">
              <Save className="h-4 w-4 mr-1" /> {saving ? "Saving..." : "Save"}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEditing(false)}>
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          </div>
        )}
      </PageHeader>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-ranch-gold" /> Document Metadata
              </CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input value={eTitle} onChange={(e) => setETitle(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Document Type</Label>
                      <Select value={eTypeId} onValueChange={setETypeId}>
                        <SelectTrigger><SelectValue placeholder="Select type..." /></SelectTrigger>
                        <SelectContent>
                          {types.map((t) => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Correspondent</Label>
                      <Select value={eCorrId} onValueChange={setECorrId}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {correspondents.map((c) => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Tags</Label>
                    <div className="flex flex-wrap gap-3 p-3 border rounded-md bg-muted/30">
                      {tags.map((tag) => (
                        <label key={tag.id} className="flex items-center gap-1.5 text-sm cursor-pointer">
                          <Checkbox
                            checked={eTagIds.includes(tag.id)}
                            onCheckedChange={() => toggleTag(tag.id)}
                          />
                          {tag.name}
                        </label>
                      ))}
                      {tags.length === 0 && (
                        <span className="text-sm text-muted-foreground">No tags available</span>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Storage Location</Label>
                      <Select value={eStorageId} onValueChange={setEStorageId}>
                        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent>
                          {storageLocations.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Date on Document</Label>
                      <Input type="date" value={eDateReceived} onChange={(e) => setEDateReceived(e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea value={eNotes} onChange={(e) => setENotes(e.target.value)} rows={3} />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Document Type</p>
                    <p className="font-medium">{typeMap.get(docTypeId) || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Correspondent</p>
                    <p className="font-medium">{corrMap.get(doc.correspondent_id ?? "") || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Storage Location</p>
                    <p className="font-medium">{slMap.get(doc.storage_location_id ?? "") || "—"}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Date on Document</p>
                    <p className="font-medium">
                      {docDate
                        ? format(parseISO(docDate + "T00:00:00"), "MMM d, yyyy")
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Processing Status</p>
                    <StatusBadge status={doc.processing_status} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Tags</p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {(doc.tag_ids ?? []).length > 0 ? (
                        (doc.tag_ids ?? []).map((tid) => (
                          <Badge key={tid} variant="outline" className="text-2xs">
                            {tagMap.get(tid) || tid}
                          </Badge>
                        ))
                      ) : (
                        <span className="text-sm text-muted-foreground">None</span>
                      )}
                    </div>
                  </div>
                  {doc.notes && (
                    <div className="col-span-2">
                      <p className="text-sm text-muted-foreground">Notes</p>
                      <p className="text-sm">{doc.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        <div>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">File Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-muted-foreground">File Name</p>
                <p className="text-sm font-medium font-mono break-all">{docFile || "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">File Size</p>
                <p className="text-sm font-medium">{formatFileSize(doc.file_size)}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">MIME Type</p>
                <p className="text-sm font-medium">{doc.mime_type || "—"}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Created</p>
                <p className="text-sm font-medium">
                  {doc.created_at
                    ? format(parseISO(doc.created_at), "MMM d, yyyy 'at' h:mm a")
                    : "—"}
                </p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Updated</p>
                <p className="text-sm font-medium">
                  {doc.updated_at
                    ? format(parseISO(doc.updated_at), "MMM d, yyyy 'at' h:mm a")
                    : "—"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}
